import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Pencil, Plus, Trash2, Zap } from "lucide-react";

import { api } from "@/lib/api";
import { parseTiers, TierEditor, toTierS } from "@/components/tier-editor";
import { Button, Card, Field } from "@/components/kit";
import { Sheet } from "@/components/sheet";
import { useToast } from "@/components/toast";
import { eur, pct } from "@/lib/format";

export function tierSummary(tiers) {
  return tiers.map((t) => (t.max !== null ? `${eur(t.min)}–${eur(t.max)} : ${pct(t.percent)}` : `≥ ${eur(t.min)} : ${pct(t.percent)}`)).join(" · ");
}

export function useTemplates() {
  return useQuery({ queryKey: ["tier-templates"], queryFn: () => api("/admin/tier-templates") });
}

function TemplateRow({ t, busy, missingCount, onEdit, onRemove, onApply }) {
  return (
    <div data-testid={`template-${t.id}`} className="flex flex-col gap-2 rounded-xl bg-[#2A2A2A] p-3">
      <div className="flex items-center gap-2">
        <h4 className="t-h3 flex-1 truncate">{t.name}</h4>
        <button type="button" aria-label="Modifier le modèle" data-testid={`template-edit-${t.id}`} onClick={onEdit} className="focus-ring flex h-11 w-11 items-center justify-center rounded-xl text-[#D4AF37] hover:bg-white/5">
          <Pencil className="h-5 w-5" />
        </button>
        <button type="button" aria-label="Supprimer le modèle" data-testid={`template-delete-${t.id}`} disabled={busy !== null} onClick={onRemove} className="focus-ring flex h-11 w-11 items-center justify-center rounded-xl text-[#EB5757] hover:bg-[#EB5757]/10 disabled:opacity-50">
          <Trash2 className="h-5 w-5" />
        </button>
      </div>
      <p className="t-caption">{tierSummary(t.tiers)}</p>
      <Button testID={`template-apply-missing-${t.id}`} title="Appliquer aux agents sans barème" icon={Zap} variant="secondary" loading={busy === `apply-${t.id}`} disabled={busy !== null || missingCount === 0} onClick={onApply} />
    </div>
  );
}

export function TemplatesSection({ missingCount }) {
  const toast = useToast();
  const qc = useQueryClient();
  const q = useTemplates();
  const [edit, setEdit] = useState(null);
  const [name, setName] = useState("");
  const [tiers, setTiers] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);

  const open = (t) => {
    setEdit(t);
    setError("");
    setName(t === "new" ? "" : t.name);
    setTiers(t === "new" ? [{ min: "0", max: "", percent: "" }] : toTierS(t.tiers));
  };

  const refresh = () => {
    ["tier-templates", "admin-dashboard", "admin-agents", "admin-agent"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };

  const save = async () => {
    setError("");
    if (!name.trim()) return setError("Donnez un nom au modèle");
    setBusy("save");
    try {
      const body = { name: name.trim(), tiers: parseTiers(tiers) };
      if (edit === "new") await api("/admin/tier-templates", { method: "POST", body });
      else if (edit) await api(`/admin/tier-templates/${edit.id}`, { method: "PUT", body });
      refresh();
      setEdit(null);
      toast("Modèle enregistré.");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const remove = async (t) => {
    setBusy(`del-${t.id}`);
    try {
      await api(`/admin/tier-templates/${t.id}`, { method: "DELETE" });
      refresh();
      toast("Modèle supprimé.", "info");
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(null);
    }
  };

  const applyMissing = async (t) => {
    setBusy(`apply-${t.id}`);
    try {
      const r = await api(`/admin/tier-templates/${t.id}/apply-missing`, { method: "POST" });
      refresh();
      toast(r.applied ? `Modèle appliqué à ${r.applied} agent(s) sans barème.` : "Aucun agent sans barème.", r.applied ? "success" : "info");
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card testID="templates-section" className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="t-h3">Modèles de barème</h2>
        <Button testID="template-add-button" title="Nouveau" icon={Plus} variant="secondary" onClick={() => open("new")} />
      </div>
      <p className="t-caption">
        Créez des barèmes types, puis appliquez-les en un clic aux agents. Vous pourrez ensuite ajuster chaque agent au cas par cas depuis sa fiche.
        {missingCount ? ` ${missingCount} agent(s) n'ont pas encore de barème.` : ""}
      </p>
      {(q.data ?? []).length === 0 ? (
        <p className="t-caption" data-testid="templates-empty">Aucun modèle pour l'instant.</p>
      ) : (
        (q.data ?? []).map((t) => (
          <TemplateRow key={t.id} t={t} busy={busy} missingCount={missingCount} onEdit={() => open(t)} onRemove={() => remove(t)} onApply={() => applyMissing(t)} />
        ))
      )}
      <Sheet visible={edit !== null} onClose={() => setEdit(null)} title={edit === "new" ? "Nouveau modèle" : "Modifier le modèle"} testID="template-sheet">
        <div className="flex flex-col gap-3">
          <Field testID="template-name-input" label="Nom du modèle" placeholder="Ex. Agent indépendant" value={name} onChangeText={(v) => { setName(v); setError(""); }} />
          <TierEditor tiers={tiers} onChange={(t) => { setTiers(t); setError(""); }} prefix="template-tier" />
          {error ? <p data-testid="template-error" className="text-sm text-[#EB5757]">{error}</p> : null}
          <Button testID="template-save-button" title="Enregistrer le modèle" loading={busy === "save"} onClick={save} />
        </div>
      </Sheet>
    </Card>
  );
}
